/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { MultiSelect } from '@eliadmin/core/src/components/ui/multi-select';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMemo } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import type { InputProps } from 'react-select';

type FieldProps<TFormValues extends FieldValues> = {
  control: Control<TFormValues>;
  name: FieldPath<TFormValues>;
  placeholder?: string;
  ariaLabel?: string;
  label?: string | React.ReactNode;
  required?: boolean;
  type?: InputProps['type'] | 'textarea';
  options?: { value: string | number; label: string }[];
  description?: string | React.ReactNode;
  isDisabled?: boolean;
  className?: {
    wrapper?: string;
  };
  menuPlacement?: 'top' | 'bottom';
};

export function Field<TFormValues extends FieldValues>(props: FieldProps<TFormValues>) {
  const content = useMemo(() => {
    if (props.type === 'select') {
      if (!props.options) {
        throw new Error('Select type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="p-[1px]">
                {props.label && <FormLabel>{props.label}</FormLabel>}
                <FormControl>
                  <Select
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                  >
                    <SelectTrigger
                      aria-label={props.ariaLabel}
                      innerTriggerClassName="px-6"
                      className="h-[56px] rounded-[8px] bg-[#f9fafb] px-0 py-0 text-sm lg:text-base"
                    >
                      <SelectValue
                        placeholder={props.placeholder}
                        className="text-sm lg:text-base"
                      />
                    </SelectTrigger>
                    <SelectContent sideOffset={4}>
                      {props.options?.map(o => (
                        <SelectItem key={o.value} value={String(o.value)}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                {props.description && (
                  <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                    {props.description}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'multiselect') {
      if (!props.options) {
        throw new Error('MultiSelect type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem className="relative min-h-[56px]">
                <FormControl>
                  <MultiSelect
                    options={props.options} // Options passed in
                    isMulti={true} // Multi select enabled
                    placeholder={props.placeholder} // Placeholder text
                    value={field.value} // Controlled field value
                    onChange={field.onChange} // Handling changes
                    isDisabled={props.isDisabled} // Disabled state
                    noOptionsMessage={() => 'No options available'} // Custom message for no options
                    stylesConfig={{}} // Custom styles if needed (optional)
                    menuPlacement={props.menuPlacement || 'bottom'}
                    classNamesConfig={{
                      valueContainer() {
                        return cn('pl-2  bg-input');
                      },
                      control() {
                        return cn('!min-h-[56px] bg-input rounded-lg');
                      },
                      menu() {
                        return cn('!z-[100] !bg-input');
                      },
                      multiValue() {
                        return cn('bg-primary text-primary-foreground');
                      },
                      menuList() {
                        return cn('!z-[100] !bg-input block');
                      },
                    }} // Custom classNames if needed (optional)
                    variant={'outlined'}
                  />
                </FormControl>
                {props.description && (
                  <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                    {props.description}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'radio') {
      if (!props.options) {
        throw new Error('Radio type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="flex flex-col gap-2 p-[1px]">
                {props.label && <FormLabel>{props.label}</FormLabel>}
                <FormControl>
                  <RadioGroup
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                    className="flex flex-row gap-4 pl-2"
                  >
                    {props.options?.map(o => (
                      <div key={o.value} className="flex gap-2">
                        <RadioGroupItem id={`radiogroupitem-${o.value}`} value={String(o.value)} />
                        <Label className="flex items-center" htmlFor={`radiogroupitem-${o.value}`}>
                          <p className="text-sm font-semibold text-foreground">{o.label}</p>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
                {props.description && (
                  <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                    {props.description}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'checkbox') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              <div className="flex w-full flex-row items-center gap-2">
                <FormControl>
                  <Checkbox
                    {...(field as any)}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={props.isDisabled}
                    aria-disabled={props.isDisabled}
                  />
                </FormControl>
                {props.label && <FormLabel>{props.label}</FormLabel>}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'tel') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              {props.label && <FormLabel>{props.label}</FormLabel>}
              <FormControl>
                <PhoneInputField
                  placeholder={props.placeholder}
                  aria-label={props.ariaLabel}
                  type={props.type}
                  {...(field as any)}
                  disabled={props.isDisabled}
                  aria-disabled={props.isDisabled}
                />
              </FormControl>
              {props.description && (
                <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                  {props.description}
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem required={props.required} className="p-[1px]">
            {props.label && <FormLabel>{props.label}</FormLabel>}
            <FormControl>
              <Input
                placeholder={props.placeholder}
                aria-label={props.ariaLabel}
                type={props.type}
                {...(field as any)}
                disabled={props.isDisabled}
                aria-disabled={props.isDisabled}
              />
            </FormControl>
            {props.description && (
              <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                {props.description}
              </p>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }, [
    props.control,
    props.isDisabled,
    props.required,
    props.label,
    props.name,
    props.options,
    props.placeholder,
    props.ariaLabel,
    props.type,
  ]);

  return <div className={cn(props.className?.wrapper)}>{content}</div>;
}
